import React, { useMemo, useState } from "react";
import { FieldValues, UseFormRegister, UseFormSetValue, UseFormTrigger, UseFormWatch } from "react-hook-form";
import moment from "moment";
import Button from "src/components/Button";
import Input, { InputTypes } from "src/components/Input";
import { useIsMobile } from "src/hooks/useIsMobile";
import { VALIDATION_MESSAGES } from "src/constants/validation";
import styled from "styled-components";
import { StyledForm } from "src/pages/ChangePassword";
import { colors } from "src/styles";
import { FormContainer } from "src/pages/DynamicFormList/styles";
import { getBase64File } from "src/utils/convertToBase64";
// import CustomToast from "src/components/atoms/CustomToast";
import BackButton from "src/components/Button/BackButton";

type ChecklistFileConfig = {
  label?: string;
  accept?: string[];
  multiple?: boolean;
  folderName?: string;
  required?: boolean;
};

type ChecklistAttachmentValue = {
  base64: string;
  contentType: string;
  fileName: string;
  folderName: string;
};

const DEFAULT_ATTACHMENT_FOLDER = "ChecklistAttachments";

interface TimeAndAttendanceInputFormProps {
  onSubmit: () => void;
  isValid: boolean;
  register: UseFormRegister<FieldValues>;
  loading: boolean;
  error?: string;
  errors?: any;
  labelMessage: string;
  validateEmployeeId?: any;
  validationError?: string;
  setValue?: UseFormSetValue<FieldValues>;
  trigger?: UseFormTrigger<FieldValues>;
  watch?: UseFormWatch<FieldValues>;
  submitSuccess: boolean;
  isToastVisible: boolean;
  locationName: string;
  clockAction?: "clockIn" | "clockOut";
  onBack: () => void;
  submitButtonText?: string;
  currentStatusMessage?: string;
  statusErrorMessage?: string;
  isStatusLoading?: boolean;
  dailySessionHistory?: Array<{
    action: "clockIn" | "clockOut";
    siteName: string;
    timestamp: string;
  }>;
  checklistsData?: {
    checklistsInfo: Array<{
      id: string;
      default: boolean;
      oneClick: boolean;
      includeInEmailReport: boolean;
      startIndex: number;
      endIndex: number;
    }>;
    questions: Array<{
      label: string;
      questions: Array<{
        label?: string;
        rules?: {
          required?: boolean;
        };
        passAnswer: {
          condition: string;
          value: string;
        };
        radio: Array<{
          label: string;
          value: string;
        }>;
        attachment?: ChecklistFileConfig;
        file?: ChecklistFileConfig;
        photo?: ChecklistFileConfig;
      }>;
    }>;
  };
}

export const TimeAndAttendanceInputForm: React.FC<TimeAndAttendanceInputFormProps> = ({
  onSubmit,
  register,
  isValid,
  loading,
  // labelMessage,
  validateEmployeeId,
  validationError,
  // submitSuccess,
  // isToastVisible,
  setValue,
  trigger,
  watch,
  locationName,
  checklistsData,
  clockAction = "clockIn",
  onBack,
  submitButtonText = "Submit",
  currentStatusMessage = "",
  statusErrorMessage = "",
  isStatusLoading = false,
  dailySessionHistory = [],
}) => {
  const { isMobile } = useIsMobile();
  const [selectedAttachments, setSelectedAttachments] = useState<Record<string, string[]>>({});

  // Extract relevant questions based on active tab
  const relevantQuestions = useMemo(() => {
    if (!checklistsData?.checklistsInfo || !checklistsData?.questions) {
      return [];
    }

    const checklist = checklistsData.checklistsInfo[0];
    if (!checklist) return [];

    const { startIndex, endIndex } = checklist;
    return checklistsData.questions.slice(startIndex, endIndex + 1).map((questionGroup: any) => {
      if (Array.isArray(questionGroup?.questions)) {
        return questionGroup;
      }

      // Some checklists can return a single question object instead of a section.
      return {
        label: questionGroup?.label || "",
        questions: [questionGroup],
      };
    });
  }, [checklistsData]);

  const getAttachmentConfig = (question: any): ChecklistFileConfig | undefined =>
    question.file || question.attachment || question.photo;

  const getRadioOptions = (question: any) =>
    Array.isArray(question?.radio)
      ? question.radio
      : Array.isArray(question?.radio?.options)
        ? question.radio.options
        : [];

  const isQuestionRequired = (question: any) => {
    const attachmentConfig = getAttachmentConfig(question);
    return Boolean(question?.rules?.required || attachmentConfig?.required);
  };

  const getAcceptValue = (attachmentConfig?: ChecklistFileConfig) => attachmentConfig?.accept?.join(",") || undefined;

  const isImageAttachment = (attachmentConfig?: ChecklistFileConfig) => {
    if (!attachmentConfig?.accept?.length) {
      return false;
    }

    return attachmentConfig.accept.some((accept) => {
      const normalized = accept.toLowerCase();
      return (
        normalized.includes("image") ||
        normalized.endsWith(".jpg") ||
        normalized.endsWith(".jpeg") ||
        normalized.endsWith(".png") ||
        normalized.endsWith(".heic")
      );
    });
  };

  const handleAttachmentChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: string,
    folderName: string,
  ) => {
    if (!event.target.files || !setValue) {
      return;
    }

    const files = Object.values(event.target.files);
    const uploadedFiles: ChecklistAttachmentValue[] = await Promise.all(
      files.map(async (file) => {
        const base64 = (await getBase64File(file)) as string;
        return {
          base64,
          contentType: file.type,
          fileName: file.name,
          folderName,
        };
      }),
    );

    setSelectedAttachments((prev) => ({
      ...prev,
      [fieldName]: files.map((file) => file.name),
    }));
    setValue(fieldName, uploadedFiles, {
      shouldDirty: true,
      shouldValidate: true,
    });
    trigger?.(fieldName);
    event.target.value = "";
  };

  const watchedValues = watch?.() || {};

  const areRequiredChecklistQuestionsAnswered = useMemo(() => {
    const requiredQuestions: Array<{ question: any; groupIndex: number; questionIndex: number }> = [];

    relevantQuestions.forEach((questionGroup: any, groupIndex: number) => {
      (questionGroup.questions || []).forEach((question: any, questionIndex: number) => {
        if (isQuestionRequired(question)) {
          requiredQuestions.push({ question, groupIndex, questionIndex });
        }
      });
    });

    if (requiredQuestions.length === 0) {
      return true;
    }

    return requiredQuestions.every(({ question, groupIndex, questionIndex }) => {
      const radioField = `checklist_${groupIndex}_${questionIndex}`;
      const attachmentField = `checklist_attachment_${groupIndex}_${questionIndex}`;

      const hasRadio = getRadioOptions(question).length > 0;
      const hasAttachment = Boolean(getAttachmentConfig(question));

      if (hasRadio && hasAttachment) {
        const radioAnswer = watchedValues[radioField];
        const attachmentAnswer = watchedValues[attachmentField];
        return Boolean(radioAnswer) && Array.isArray(attachmentAnswer) && attachmentAnswer.length > 0;
      }

      if (hasRadio) {
        return Boolean(watchedValues[radioField]);
      }

      if (hasAttachment) {
        const attachmentAnswer = watchedValues[attachmentField];
        return Array.isArray(attachmentAnswer) && attachmentAnswer.length > 0;
      }

      return true;
    });
  }, [relevantQuestions, watchedValues]);

  return (
    <>
      <Content>
        <ContentFrame $isMobile={isMobile}>
          <LocationTitleContainer>
            <LocationTitle>{locationName}</LocationTitle>
          </LocationTitleContainer>
          <Header>
            <HeaderContainer>
              <BackButton backHandler={onBack} additionalStyles={{ marginBottom: 0 }} />
              <HeaderText>{clockAction === "clockIn" ? "Clock In" : "Clock Out"}</HeaderText>
              <EmptyContainer />
            </HeaderContainer>
          </Header>

          <FormContainer>
            {/* <FormLabel>{labelMessage}</FormLabel> */}

            <StatusSummary>
              <StatusTitle>Current Status</StatusTitle>
              {isStatusLoading ? (
                <StatusText>Loading current clock status...</StatusText>
              ) : (
                <StatusText>{currentStatusMessage}</StatusText>
              )}
              {statusErrorMessage ? <StatusWarning>{statusErrorMessage}</StatusWarning> : null}

              {dailySessionHistory.length > 0 && (
                <StatusHistory>
                  {dailySessionHistory.map((session, index) => (
                    <li key={`${session.action}-${session.timestamp}-${index}`}>
                      {`${session.action === "clockIn" ? "Clocked in" : "Clocked out"} ${session.siteName} ${moment(session.timestamp).format("h:mm A")}`}
                    </li>
                  ))}
                </StatusHistory>
              )}
            </StatusSummary>

            <InputForm $isMobile={isMobile}>
              <Input
                controlId="employee-id"
                type={InputTypes.text}
                label="Employee ID"
                validation={register("employeeId", {
                  required: {
                    value: true,
                    message: VALIDATION_MESSAGES.required,
                  },
                  onBlur: (e) => validateEmployeeId(e.target.value),
                })}
              />

              {validationError && <ValidationError>{validationError}</ValidationError>}

              <Input
                controlId="first-name"
                type={InputTypes.text}
                label="First Name"
                validation={register("firstName", {
                  required: {
                    value: true,
                    message: VALIDATION_MESSAGES.required,
                  },
                })}
                disabled={true}
              />

              <Input
                controlId="last-name"
                type={InputTypes.text}
                label="Last Name"
                validation={register("lastName", {
                  required: {
                    value: true,
                    message: VALIDATION_MESSAGES.required,
                  },
                })}
                disabled={true}
              />

              {/* Render Checklist Questions */}
              {clockAction === "clockIn" && relevantQuestions.length > 0 && (
                <ChecklistSection>
                  {relevantQuestions.map((questionGroup, groupIndex) => (
                    <QuestionGroupContainer key={groupIndex}>
                      <QuestionGroupLabel>{questionGroup.label}</QuestionGroupLabel>

                      {(questionGroup.questions || []).map((question: any, qIndex: number) => (
                        <QuestionContainer key={qIndex}>
                          {question.label && <QuestionLabel>{question.label}</QuestionLabel>}

                          {getRadioOptions(question).length > 0 && (
                            <RadioGroupContainer>
                              {getRadioOptions(question).map((option: any) => (
                                <RadioOption key={option.value}>
                                  <RadioInput
                                    type="radio"
                                    id={`q-${groupIndex}-${qIndex}-${option.value}`}
                                    value={option.value}
                                    {...register(`checklist_${groupIndex}_${qIndex}`, {
                                      required: isQuestionRequired(question) ? VALIDATION_MESSAGES.required : false,
                                    })}
                                  />
                                  <RadioLabel htmlFor={`q-${groupIndex}-${qIndex}-${option.value}`}>
                                    {option.label}
                                  </RadioLabel>
                                </RadioOption>
                              ))}
                            </RadioGroupContainer>
                          )}

                          {getAttachmentConfig(question) && (
                            <AttachmentContainer>
                              <AttachmentCard>
                                <AttachmentHint>
                                  Upload files for this checklist item.
                                  {isImageAttachment(getAttachmentConfig(question))
                                    ? " Images are supported."
                                    : " Documents are supported."}
                                </AttachmentHint>

                                <AttachmentActionRow>
                                  <AttachmentActionLabel htmlFor={`checklist_file_${groupIndex}_${qIndex}`}>
                                    Add File
                                  </AttachmentActionLabel>

                                  {isMobile && isImageAttachment(getAttachmentConfig(question)) && (
                                    <AttachmentActionLabel htmlFor={`checklist_camera_${groupIndex}_${qIndex}`}>
                                      Use Camera
                                    </AttachmentActionLabel>
                                  )}
                                </AttachmentActionRow>

                                <AttachmentInput
                                  id={`checklist_file_${groupIndex}_${qIndex}`}
                                  type="file"
                                  accept={getAcceptValue(getAttachmentConfig(question))}
                                  multiple={!!getAttachmentConfig(question)?.multiple}
                                  onChange={(event) =>
                                    handleAttachmentChange(
                                      event,
                                      `checklist_attachment_${groupIndex}_${qIndex}`,
                                      getAttachmentConfig(question)?.folderName || DEFAULT_ATTACHMENT_FOLDER,
                                    )
                                  }
                                />

                                {isMobile && isImageAttachment(getAttachmentConfig(question)) && (
                                  <AttachmentInput
                                    id={`checklist_camera_${groupIndex}_${qIndex}`}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    multiple={false}
                                    onChange={(event) =>
                                      handleAttachmentChange(
                                        event,
                                        `checklist_attachment_${groupIndex}_${qIndex}`,
                                        getAttachmentConfig(question)?.folderName || DEFAULT_ATTACHMENT_FOLDER,
                                      )
                                    }
                                  />
                                )}

                                <AttachmentNames>
                                  {selectedAttachments[`checklist_attachment_${groupIndex}_${qIndex}`]?.length > 0 ? (
                                    selectedAttachments[`checklist_attachment_${groupIndex}_${qIndex}`].map(
                                      (fileName, fileIndex) => (
                                        <AttachmentChip key={`${fileName}-${fileIndex}`}>{fileName}</AttachmentChip>
                                      ),
                                    )
                                  ) : (
                                    <AttachmentEmpty>No file selected</AttachmentEmpty>
                                  )}
                                </AttachmentNames>

                                {isQuestionRequired(question) &&
                                  !Array.isArray(watchedValues[`checklist_attachment_${groupIndex}_${qIndex}`]) && (
                                    <AttachmentRequiredText>{VALIDATION_MESSAGES.required}</AttachmentRequiredText>
                                  )}
                              </AttachmentCard>
                            </AttachmentContainer>
                          )}
                        </QuestionContainer>
                      ))}
                    </QuestionGroupContainer>
                  ))}
                </ChecklistSection>
              )}
            </InputForm>

            <ButtonContainer>
              <ButtonWrapper>
                <Button
                  text={submitButtonText}
                  type="submit"
                  disabled={
                    !isValid || isStatusLoading || (clockAction === "clockIn" && !areRequiredChecklistQuestionsAnswered)
                  }
                  loading={loading}
                  onClick={onSubmit}
                  additionalStyles={{ height: "48px" }}
                />
              </ButtonWrapper>
            </ButtonContainer>
          </FormContainer>
        </ContentFrame>
      </Content>

      {/* <CustomToast
        success={submitSuccess}
        isToastVisible={isToastVisible}
        title={<strong>{submitSuccess ? "Success" : "Error"}</strong>}
        message={<p>{labelMessage}</p>}
      /> */}
    </>
  );
};

// Styled Components
const Content = styled.div`
  padding-top: 120px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;

  @media screen and (max-width: 1369px) {
    padding-top: 100px;
  }
`;

const LocationTitleContainer = styled.div`
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e0e0e0;
`;

const LocationTitle = styled.h1`
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 24px;
  color: #333333;
  margin: 0;
  text-align: center;

  @media screen and (max-width: 768px) {
    font-size: 20px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ButtonWrapper = styled.div`
  width: 100%;
  max-width: 617px;
  display: flex;
  flex-direction: column-reverse;
`;

const Header = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
`;

const HeaderContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 617px;
`;

const HeaderText = styled.h2`
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 22px;
  color: #333333;
  text-align: center;
`;

const StatusSummary = styled.div`
  width: 100%;
  max-width: 617px;
  margin: 20px auto 20px;
  padding: 14px;
  border: 1px solid #dbe5e1;
  border-radius: 8px;
  background-color: #f7faf9;
  text-align: center;
`;

const StatusTitle = styled.h3`
  margin: 0 0 8px;
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  color: #1f2d2a;
`;

const StatusText = styled.p`
  margin: 0;
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #1f2d2a;
`;

const StatusWarning = styled.p`
  margin: 8px 0 0;
  font-family: "Roboto", sans-serif;
  font-size: 13px;
  color: #9e5b00;
`;

const StatusHistory = styled.ul`
  margin: 10px 0 0;
  padding-left: 0;
  list-style: none;

  li {
    margin-bottom: 4px;
    font-family: "Roboto", sans-serif;
    font-size: 13px;
    color: #374541;
  }
`;

const EmptyContainer = styled.div`
  width: 40px; /* Adjust width as needed to match BackButton width */
`;

// const FormLabel = styled.p`
//   font-family: "Roboto";
//   font-style: normal;
//   font-weight: 500;
//   font-size: 20px;
//   text-align: center;
//   color: #555555;
// `;

const InputForm = styled(StyledForm)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  div {
    max-width: 617px;
    text-align: left;
  }
`;

const ContentFrame = styled.div<{ $isMobile?: boolean }>`
  padding: 30px;
  background-color: ${colors.white.default};
  box-shadow: 0px 4px 120px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  width: ${({ $isMobile }) => ($isMobile ? "100%" : "60%")};
`;

const ChecklistSection = styled.div`
  width: 100%;
  max-width: 617px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
`;

const QuestionGroupContainer = styled.div`
  margin-bottom: 20px;
`;

const QuestionContainer = styled.div`
  margin-bottom: 12px;
`;

const QuestionLabel = styled.p`
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #444444;
  margin: 0 0 8px 0;
`;

const QuestionGroupLabel = styled.h3`
  font-family: "Roboto", sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #333333;
  margin: 0 0 12px 0;
`;

const RadioGroupContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 12px;
`;

const RadioOption = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RadioInput = styled.input`
  cursor: pointer;
  width: 18px;
  height: 18px;
  accent-color: #4caf50;

  &:hover {
    transform: scale(1.1);
  }
`;

const RadioLabel = styled.label`
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #555555;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: #333333;
  }
`;

const AttachmentContainer = styled.div`
  margin-top: 8px;
`;

const AttachmentCard = styled.div`
  border: 1px solid #d9e3e0;
  border-radius: 10px;
  padding: 12px;
  background: #f8fbfa;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AttachmentHint = styled.p`
  font-family: "Roboto", sans-serif;
  font-size: 13px;
  color: #4b5a55;
  margin: 0;
`;

const AttachmentActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const AttachmentActionLabel = styled.label`
  padding: 8px 14px;
  border-radius: 6px;
  border: 1px solid #2e7d32;
  color: #2e7d32;
  font-family: "Roboto", sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  background-color: #ffffff;

  &:hover {
    background-color: #e8f5e9;
  }
`;

const AttachmentInput = styled.input`
  display: none;
`;

const AttachmentNames = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const AttachmentChip = styled.span`
  padding: 6px 10px;
  border-radius: 20px;
  background: #e3f2fd;
  color: #0d47a1;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: "Roboto", sans-serif;
  font-size: 13px;
`;

const AttachmentEmpty = styled.span`
  font-family: "Roboto", sans-serif;
  font-size: 13px;
  color: #66756f;
`;

const AttachmentRequiredText = styled.span`
  font-family: "Roboto", sans-serif;
  font-size: 12px;
  color: #d32f2f;
`;

const ValidationError = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-bottom: 12px;
`;
